import { useEffect, useMemo, useRef, useState } from "react";
import { Bookmark, Copy, Check, Coins, ShieldCheck, Sparkles, ExternalLink } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// Build the bookmarklet payload: fetch our injector script and eval it.
// Using fetch+eval keeps the bookmarklet short and lets us update behavior
// without users re-installing.
function buildBookmarklet(origin) {
  const url = `${origin}/newseze-injector.js`;
  const code =
    `(function(){window.__newsezeOrigin=${JSON.stringify(origin)};` +
    `var s=document.createElement('script');s.src=${JSON.stringify(url)};` +
    `s.async=true;document.documentElement.appendChild(s);})();`;
  return `javascript:${encodeURIComponent(code)}`;
}

export default function Extension() {
  const origin = window.location.origin;
  const href = useMemo(() => buildBookmarklet(origin), [origin]);
  const [copied, setCopied] = useState(false);
  const anchorRef = useRef(null);

  // React blocks `javascript:` URLs in href props. Set it post-mount via the
  // DOM API so the bookmarklet payload survives drag-to-bookmarks-bar.
  useEffect(() => {
    if (anchorRef.current) anchorRef.current.setAttribute("href", href);
  }, [href]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // fallback: select the link
      window.prompt("Copy this bookmarklet:", href);
    }
  };

  return (
    <>
      <Header onOpenBrief={() => {}} />
      <main className="max-w-[1100px] mx-auto px-5 py-10" data-testid="extension-page">
        <div className="section-title">
          <span className="pip" />
          <h2>Newseze Anywhere · The Earn-Credits Bookmarklet</h2>
        </div>
        <p className="text-[15px] text-[var(--muted)] max-w-[780px] mt-2" data-testid="extension-tagline">
          One drag installs Newseze on every website you already visit. Reading Fox News?
          The New York Times? A small-town blog? Click the bookmark and a slim Newseze bar
          pins to the top of the page — earn attention credits, rate the page for trust,
          and pipe the story into Newseze AI for a balanced read.
        </p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Drag target */}
          <div className="rounded-2xl border border-[var(--line)] bg-white p-6">
            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mb-3 flex items-center gap-2">
              <Bookmark size={13} /> Step 1 · Show your bookmarks bar
            </div>
            <p className="text-[13.5px] text-[var(--ink)] mb-4">
              In Chrome, Edge, or Firefox: press <kbd className="bg-[var(--bg)] px-1.5 py-0.5 rounded border border-[var(--line)] text-[11px] font-bold">Ctrl/Cmd</kbd> + <kbd className="bg-[var(--bg)] px-1.5 py-0.5 rounded border border-[var(--line)] text-[11px] font-bold">Shift</kbd> + <kbd className="bg-[var(--bg)] px-1.5 py-0.5 rounded border border-[var(--line)] text-[11px] font-bold">B</kbd>. In Safari: View → Show Favorites Bar.
            </p>

            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mt-6 mb-3 flex items-center gap-2">
              <Bookmark size={13} /> Step 2 · Drag the button below to your bookmarks bar
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a
                ref={anchorRef}
                href="#newseze-bookmarklet"
                onClick={(e) => e.preventDefault()}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white font-black text-[14px] tracking-[-0.01em] shadow-lg cursor-grab active:cursor-grabbing"
                data-testid="extension-bookmarklet-link"
                draggable
              >
                <Coins size={16} className="text-[var(--gold)]" /> Earn with Newseze
              </a>
              <button
                onClick={copy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] text-[12px] font-black uppercase tracking-[0.14em]"
                data-testid="extension-copy-link"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy bookmarklet"}
              </button>
            </div>
            <p className="text-[12px] text-[var(--muted)] mt-3 italic">
              Drag the dark button up to your bookmarks bar. Don't click it on this page —
              Newseze is already here.
            </p>

            <div className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--blue)] mt-8 mb-3 flex items-center gap-2">
              <Bookmark size={13} /> Step 3 · Click "Earn with Newseze" on any news site
            </div>
            <p className="text-[13.5px] text-[var(--ink)]">
              Visit Fox, CNN, BBC, your local paper — anywhere. Click the bookmark and the
              Newseze bar drops in. Credits accrue automatically after 20 seconds of
              honest reading.
            </p>
          </div>

          {/* Benefits + safety */}
          <aside className="space-y-3">
            <div className="rounded-2xl bg-gradient-to-br from-[#0a1f3d] to-[#1e40af] text-white p-5" data-testid="extension-benefits">
              <div className="flex items-center gap-2 text-[var(--gold)] font-black text-[12px] uppercase tracking-[0.16em]">
                <Coins size={14} /> What you earn
              </div>
              <ul className="text-[13px] mt-2 space-y-1.5 leading-snug">
                <li>+10 credits after 20s on any external page (article cap)</li>
                <li>+5 credits when you trust-rate a page</li>
                <li>+30 credits when you pipe the page into Newseze AI</li>
                <li>Honest-read streak counts wherever you go</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-[var(--line)] bg-white p-5">
              <div className="flex items-center gap-2 text-[var(--blue)] font-black text-[12px] uppercase tracking-[0.16em]">
                <ShieldCheck size={14} /> Safety & privacy
              </div>
              <ul className="text-[12.5px] mt-2 space-y-1.5 leading-snug text-[var(--ink)]">
                <li>The bar only loads when you click the bookmark — never auto-injects.</li>
                <li>We don't read page content. We send the URL + your Newseze email to award credits.</li>
                <li>Hit the × to remove the bar at any time.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-[var(--line)] bg-[#fff7d6] p-5">
              <div className="flex items-center gap-2 text-[#7a5b00] font-black text-[12px] uppercase tracking-[0.16em]">
                <Sparkles size={14} /> Pro perk
              </div>
              <p className="text-[12.5px] mt-2 text-[var(--ink)]">
                Newseze Pro removes the daily cap on bookmarklet credits and unlocks the
                "Ask Newseze about this page" mode — get a balanced read of any article in two
                clicks.
              </p>
            </div>

            <a
              href="/networks"
              className="block text-center rounded-full border border-[var(--line)] py-2 text-[12px] font-black uppercase tracking-[0.14em] hover:bg-[var(--bg)]"
              data-testid="extension-networks-link"
            >
              <ExternalLink size={12} className="inline mr-1" /> Try inside Networks portal
            </a>
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
}
